// Extension options input — structured input for contract extension periods.

import { Plus, Trash2 } from "lucide-react";
import { Button } from "../common";
import type { ExtensionOptions, DurationUnit } from "../../types";

interface ExtensionOptionsInputProps {
  value?: ExtensionOptions;
  onChange: (value: ExtensionOptions | undefined) => void;
  disabled?: boolean;
}

const defaultExtension: ExtensionOptions = {
  durationValue: 1,
  durationUnit: "years",
  periods: [{ periodNumber: 1, exercised: false }],
};

export default function ExtensionOptionsInput({
  value,
  onChange,
  disabled,
}: ExtensionOptionsInputProps) {
  if (!value) {
    return (
      <div>
        <label className="label">Extension Options</label>
        <Button
          variant="ghost"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => onChange(defaultExtension)}
          disabled={disabled}
        >
          Add Extension Options
        </Button>
      </div>
    );
  }

  const handleRemoveAll = () => onChange(undefined);

  const handleFieldChange = (
    field: keyof ExtensionOptions,
    fieldValue: number | DurationUnit,
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handlePeriodExercised = (index: number, exercised: boolean) => {
    const periods = [...value.periods];
    periods[index] = {
      ...periods[index],
      exercised,
      exercisedDate: exercised
        ? new Date().toISOString().slice(0, 10)
        : undefined,
    };
    onChange({ ...value, periods });
  };

  const addPeriod = () => {
    const next = value.periods.length + 1;
    onChange({
      ...value,
      periods: [...value.periods, { periodNumber: next, exercised: false }],
    });
  };

  const removePeriod = (index: number) => {
    const periods = value.periods
      .filter((_, i) => i !== index)
      .map((p, i) => ({ ...p, periodNumber: i + 1 }));
    if (periods.length === 0) {
      onChange(undefined);
    } else {
      onChange({ ...value, periods });
    }
  };

  return (
    <fieldset className="space-y-3 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <legend className="label">Extension Options</legend>
        <Button
          variant="ghost"
          iconOnly
          size="sm"
          icon={<Trash2 className="h-4 w-4" />}
          aria-label="Remove extension options"
          onClick={handleRemoveAll}
          disabled={disabled}
        />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="label text-caption">Duration</label>
          <input
            type="number"
            min={1}
            value={value.durationValue}
            onChange={(e) =>
              handleFieldChange(
                "durationValue",
                parseInt(e.target.value, 10) || 1,
              )
            }
            className="input"
            disabled={disabled}
          />
        </div>
        <div className="flex-1">
          <label className="label text-caption">Unit</label>
          <select
            value={value.durationUnit}
            onChange={(e) =>
              handleFieldChange("durationUnit", e.target.value as DurationUnit)
            }
            className="input"
            disabled={disabled}
          >
            <option value="days">Days</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="label text-caption">Notice (days)</label>
          <input
            type="number"
            min={0}
            value={value.noticePeriodDays ?? ""}
            onChange={(e) =>
              handleFieldChange(
                "noticePeriodDays",
                parseInt(e.target.value, 10) || 0,
              )
            }
            className="input"
            disabled={disabled}
            placeholder="Optional"
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="label text-caption">Periods</label>
        {value.periods.map((period, index) => (
          <div
            key={period.periodNumber}
            className="flex items-center gap-3 text-body-sm"
          >
            <span className="text-slate-500 dark:text-slate-400 w-20">
              Period {period.periodNumber}
            </span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={period.exercised}
                onChange={(e) => handlePeriodExercised(index, e.target.checked)}
                disabled={disabled}
                className="rounded border-slate-300 dark:border-slate-600"
              />
              <span className="text-slate-700 dark:text-slate-300">
                Exercised
              </span>
            </label>
            {period.exercised && period.exercisedDate && (
              <span className="text-slate-500 dark:text-slate-400">
                {period.exercisedDate}
              </span>
            )}
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              icon={<Trash2 className="h-3.5 w-3.5" />}
              aria-label={`Remove period ${period.periodNumber}`}
              onClick={() => removePeriod(index)}
              disabled={disabled}
            />
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={addPeriod}
          disabled={disabled}
        >
          Add Period
        </Button>
      </div>
    </fieldset>
  );
}
